import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CriticalNotificationsPanel = () => {
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'equipment',
      severity: 'high',
      title: 'Equipment Calibration Overdue',
      message: 'UV Spectrophotometer (UV-001) calibration expired 1 day ago',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
      actionRequired: true,
      acknowledged: false
    },
    {
      id: 2,
      type: 'quality',
      severity: 'high',
      title: 'Failed Batch Test',
      message: 'Batch QA-2024-1247 failed purity analysis - immediate review required',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
      actionRequired: true,
      acknowledged: false
    },
    {
      id: 3,
      type: 'regulatory',
      severity: 'medium',
      title: 'Submission Deadline Approaching',
      message: 'EMA submission for Product XYZ-123 due in 5 days',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      actionRequired: false,
      acknowledged: false
    },
    {
      id: 4,
      type: 'safety',
      severity: 'medium',
      title: 'Adverse Event Classification',
      message: 'New adverse event report AE-2024-0892 requires severity classification',
      timestamp: new Date(Date.now() - 45 * 60 * 1000),
      actionRequired: true,
      acknowledged: false
    },
    {
      id: 5,
      type: 'blockchain',
      severity: 'low',
      title: 'Network Performance',
      message: 'Blockchain network latency higher than normal (avg 2.3s)',
      timestamp: new Date(Date.now() - 30 * 60 * 1000),
      actionRequired: false,
      acknowledged: true
    }
  ]);

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'high':
        return {
          color: 'text-error',
          bgColor: 'bg-error/5',
          borderColor: 'border-l-error',
          icon: 'AlertTriangle'
        };
      case 'medium':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning/5',
          borderColor: 'border-l-warning',
          icon: 'AlertCircle'
        };
      case 'low':
        return {
          color: 'text-primary',
          bgColor: 'bg-primary/5',
          borderColor: 'border-l-primary',
          icon: 'Info'
        };
      default:
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          borderColor: 'border-l-muted',
          icon: 'Circle'
        };
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'equipment': return 'Settings';
      case 'quality': return 'FlaskConical';
      case 'regulatory': return 'CheckCircle';
      case 'safety': return 'Shield';
      case 'blockchain': return 'Blocks';
      default: return 'Bell';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const handleAcknowledge = (notificationId) => {
    setNotifications(prev => prev?.map(notification => 
      notification?.id === notificationId 
        ? { ...notification, acknowledged: true }
        : notification
    ));
  };

  const handleDismiss = (notificationId) => {
    setNotifications(prev => prev?.filter(notification => notification?.id !== notificationId));
  };

  const unacknowledgedCount = notifications?.filter(n => !n?.acknowledged)?.length;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Icon name="Bell" size={20} className="text-foreground" />
            {unacknowledgedCount > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-error text-error-foreground text-xs rounded-full flex items-center justify-center font-medium">
                {unacknowledgedCount}
              </span>
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Critical Notifications</h3>
            <p className="text-sm text-muted-foreground">
              {unacknowledgedCount} unacknowledged alerts requiring attention
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm">
          View All
        </Button>
      </div>
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {notifications?.slice(0, 4)?.map((notification) => {
          const severityConfig = getSeverityConfig(notification?.severity);
          return (
            <div
              key={notification?.id}
              className={`p-4 border-l-4 ${severityConfig?.borderColor} ${severityConfig?.bgColor} ${
                notification?.acknowledged ? 'opacity-60' : ''
              } rounded-r-lg hover:bg-muted/30 transition-colors duration-150`}
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                  <Icon 
                    name={getTypeIcon(notification?.type)} 
                    size={16} 
                    className={severityConfig?.color}
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="text-sm font-medium text-foreground truncate">
                      {notification?.title}
                    </h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${severityConfig?.bgColor} ${severityConfig?.color} font-medium uppercase`}>
                      {notification?.severity}
                    </span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                    {notification?.message}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {formatTimeAgo(notification?.timestamp)}
                    </span>
                    
                    {!notification?.acknowledged && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleAcknowledge(notification?.id)}
                          className="text-xs text-primary hover:text-primary/80 font-medium transition-colors duration-150"
                        >
                          Acknowledge
                        </button>
                        {notification?.actionRequired && (
                          <button className="text-xs text-success hover:text-success/80 font-medium transition-colors duration-150">
                            Take Action
                          </button>
                        )}
                        <button
                          onClick={() => handleDismiss(notification?.id)}
                          className="text-muted-foreground hover:text-foreground transition-colors duration-150"
                        >
                          <Icon name="X" size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {notifications?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-3" />
          <p className="text-muted-foreground">No critical notifications</p>
          <p className="text-sm text-muted-foreground">All systems operating normally</p>
        </div>
      )}
    </div>
  );
};

export default CriticalNotificationsPanel;